import { type FocusEvent, useCallback, useEffect, useRef, useState } from 'react';

const PIN_KEY = 'fs.rail.pinned';
/** Below this width the rail is a drawer opened from the top bar's menu button. */
// eslint-disable-next-line no-restricted-syntax -- media queries cannot read CSS variables; app.css uses the same 999px.
const COMPACT_QUERY = '(max-width: 999px)';
/** Hover intent before the collapsed rail expands over the page, and the grace period before it closes. */
const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 180;

function readPinned(): boolean {
  try {
    return localStorage.getItem(PIN_KEY) === 'true';
  } catch {
    return false;
  }
}

const matchesCompact = () => typeof window.matchMedia === 'function' && window.matchMedia(COMPACT_QUERY).matches;

/** Whether the viewport is narrow; crossing the breakpoint calls `setOpen(false)` (a stable state setter). */
function useCompact(setOpen: (open: boolean) => void): boolean {
  const [compact, setCompact] = useState(matchesCompact);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(COMPACT_QUERY);
    const update = () => {
      setCompact(query.matches);
      setOpen(false);
    };
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [setOpen]);
  return compact;
}

/**
 * The Tangram rail: a 72 px icon rail that expands over the page (no layout shift) after a short
 * hover or when keyboard focus enters it, docks when pinned (the pin is a per-browser preference),
 * closes on Escape, and becomes a drawer on narrow screens.
 */
export function useRail() {
  const [open, setOpen] = useState(false);
  // Crossing the breakpoint closes whatever was open.
  const compact = useCompact(setOpen);
  const [pinned, setPinnedState] = useState(readPinned);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clear = () => clearTimeout(timer.current);
  useEffect(() => {
    const pending = timer;
    return () => clearTimeout(pending.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const hoverable = !compact && !pinned;

  const setPinned = useCallback((value: boolean) => {
    setPinnedState(value);
    setOpen(false);
    try {
      localStorage.setItem(PIN_KEY, String(value));
    } catch {
      // The pin is a convenience only.
    }
  }, []);

  return {
    compact,
    pinned: pinned && !compact,
    open,
    setPinned,
    toggleDrawer: () => setOpen((value) => !value),
    close: () => {
      clear();
      setOpen(false);
    },
    railProps: {
      onMouseEnter: () => {
        if (!hoverable) return;
        clear();
        timer.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
      },
      onMouseLeave: (event: { currentTarget: HTMLElement }) => {
        clear();
        if (!hoverable) return;
        // Keep it open while keyboard focus is inside.
        if (event.currentTarget.querySelector(':focus-visible')) return;
        timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
      },
      onFocus: (event: FocusEvent<HTMLElement>) => {
        if (hoverable && event.target.matches(':focus-visible')) setOpen(true);
      },
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      },
    },
  };
}
